// src/utils/tokenManager.js
/**
 * JWT Token Management Utility
 * Handles secure token storage, validation, and automatic refresh
 */

// Token storage keys
const TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const TOKEN_EXPIRY_KEY = 'tokenExpiry';

/**
 * Decode JWT token without verification (client-side only for expiry check)
 * @param {string} token - JWT token
 * @returns {Object|null} Decoded payload or null if invalid
 */
export const decodeJWT = (token) => {
  if (!token || typeof token !== 'string') return null;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if expired
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;
  
  // Add 5 minute buffer before actual expiry
  const expiryTime = decoded.exp * 1000;
  const bufferTime = 5 * 60 * 1000; // 5 minutes
  const now = Date.now();
  
  return now >= (expiryTime - bufferTime);
};

/**
 * Get token expiry time
 * @param {string} token - JWT token
 * @returns {number|null} Expiry timestamp or null
 */
export const getTokenExpiry = (token) => {
  const decoded = decodeJWT(token);
  return decoded?.exp ? decoded.exp * 1000 : null;
};

/**
 * Store tokens securely
 * @param {string} accessToken - Access token
 * @param {string} refreshToken - Refresh token (optional)
 */
export const storeTokens = (accessToken, refreshToken = null) => {
  if (!accessToken) {
    console.error('Access token is required');
    return;
  }

  try {
    // Store access token
    localStorage.setItem(TOKEN_KEY, accessToken);
    
    // Store expiry time for quick check
    const expiry = getTokenExpiry(accessToken);
    if (expiry) {
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
    }
    
    // Store refresh token if provided
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    
    console.log('Tokens stored successfully');
  } catch (error) {
    console.error('Error storing tokens:', error);
  }
};

/**
 * Get stored access token
 * @returns {string|null} Access token or null
 */
export const getAccessToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('Error retrieving access token:', error);
    return null;
  }
};

/**
 * Get stored refresh token
 * @returns {string|null} Refresh token or null
 */
export const getRefreshToken = () => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Error retrieving refresh token:', error);
    return null;
  }
};

/**
 * Clear all stored tokens
 */
export const clearTokens = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    console.log('Tokens cleared successfully');
  } catch (error) {
    console.error('Error clearing tokens:', error);
  }
};

/**
 * Check if user has valid authentication
 * @returns {boolean} True if authenticated with valid token
 */
export const isAuthenticated = () => {
  const token = getAccessToken();
  return token && !isTokenExpired(token);
};

/**
 * Check if token needs refresh (within 10 minutes of expiry)
 * @returns {boolean} True if needs refresh
 */
export const needsTokenRefresh = () => {
  const token = getAccessToken();
  if (!token) return false;
  
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return false;
  
  // Refresh if within 10 minutes of expiry
  const expiryTime = decoded.exp * 1000;
  const refreshThreshold = 10 * 60 * 1000; // 10 minutes
  const now = Date.now();
  
  return now >= (expiryTime - refreshThreshold);
};

/**
 * Get user information from token
 * @returns {Object|null} User info or null
 */
export const getUserFromToken = () => {
  const token = getAccessToken();
  if (!token) return null;
  
  const decoded = decodeJWT(token);
  if (!decoded) return null;
  
  // Extract common user fields (adjust based on your JWT structure)
  return {
    id: decoded.sub || decoded.userId || decoded.id,
    email: decoded.email,
    firstName: decoded.firstName || decoded.given_name,
    lastName: decoded.lastName || decoded.family_name,
    roles: decoded.roles || decoded.authorities || [],
    permissions: decoded.permissions || [],
    exp: decoded.exp,
    iat: decoded.iat
  };
};

/**
 * Token refresh mechanism
 * @param {Function} refreshFunction - Function that handles token refresh API call
 * @returns {Promise<boolean>} True if refresh successful
 */
export const refreshTokens = async (refreshFunction) => {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    console.warn('No refresh token available');
    return false;
  }
  
  try {
    console.log('Refreshing tokens...');
    const response = await refreshFunction(refreshToken);
    
    if (response.accessToken) {
      storeTokens(response.accessToken, response.refreshToken || refreshToken);
      console.log('Tokens refreshed successfully');
      return true;
    }
    
    console.error('No access token in refresh response');
    return false;
  } catch (error) {
    console.error('Token refresh failed:', error);
    // Clear tokens on refresh failure
    clearTokens();
    return false;
  }
};

/**
 * Initialize token validation on app start
 * Clears expired tokens automatically
 */
export const initializeTokenValidation = () => {
  const token = getAccessToken();
  
  if (token && isTokenExpired(token)) {
    console.log('Token expired on initialization, clearing...');
    clearTokens();
  }
};

/**
 * Setup automatic token refresh
 * @param {Function} refreshFunction - Function to call for token refresh
 * @param {number} checkInterval - Check interval in milliseconds (default: 5 minutes)
 * @returns {number} Interval ID for cleanup
 */
export const setupAutoRefresh = (refreshFunction, checkInterval = 5 * 60 * 1000) => {
  return setInterval(async () => {
    if (needsTokenRefresh()) {
      try {
        await refreshTokens(refreshFunction);
      } catch (error) {
        console.error('Auto refresh failed:', error);
      }
    }
  }, checkInterval);
};

/**
 * Cleanup auto refresh interval
 * @param {number} intervalId - Interval ID to clear
 */
export const cleanupAutoRefresh = (intervalId) => {
  if (intervalId) {
    clearInterval(intervalId);
  }
};

export default {
  decodeJWT,
  isTokenExpired,
  getTokenExpiry,
  storeTokens,
  getAccessToken,
  getRefreshToken,
  clearTokens,
  isAuthenticated,
  needsTokenRefresh,
  getUserFromToken,
  refreshTokens,
  initializeTokenValidation,
  setupAutoRefresh,
  cleanupAutoRefresh
};